import { MagnifyingGlassIcon } from '@phosphor-icons/react'
import { useState } from 'react'

import { UnsplashImage, useGetUnsplashImages } from '@/api/unsplash/get-unsplash-images'
import { Button } from '@/components/ui/button'
import { TextInput } from '@/components/ui/text-input'
import { Typography } from '@/components/ui/typography'

import { SKELETON_COUNT } from './browse-library-modal.constants'
import * as S from './browse-library-modal.styled'
import { BrowseLibraryModalProps } from './browse-library-modal.types'

export const BrowseLibraryModal = ({
  onClose,
  onSelect,
  initialSearch,
}: BrowseLibraryModalProps) => {
  const [search, setSearch] = useState(initialSearch ?? '')
  const [selectedImage, setSelectedImage] = useState<UnsplashImage | null>(null)

  const { data, isFetching } = useGetUnsplashImages(search)
  const images = data?.pictures ?? []

  const handleSelectPhoto = async () => {
    if (!selectedImage) return

    const response = await fetch(selectedImage.url)
    const blob = await response.blob()
    const file = new File([blob], `${selectedImage.id}.jpg`, {
      type: blob.type || 'image/jpeg',
    })

    onSelect(file)
    onClose()
  }

  const showEmpty = !search || (!isFetching && images.length === 0)

  return (
    <S.BrowseModal
      title="Browse Library"
      footer={
        <S.FooterWrap>
          <Button use="primary" size="medium" disabled={!selectedImage} onClick={handleSelectPhoto}>
            Select
          </Button>
        </S.FooterWrap>
      }
      onClose={onClose}
    >
      <S.SearchWrapper>
        <TextInput
          autoFocus
          clearable
          placeholder="Search images..."
          icon={MagnifyingGlassIcon}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </S.SearchWrapper>

      {showEmpty ? (
        <S.EmptyStateWrapper>
          <Typography size="text-medium" color="black500" textAlign="center">
            {search ? 'No images found.' : 'Start typing to search images.'}
          </Typography>
        </S.EmptyStateWrapper>
      ) : (
        <S.ImageGrid>
          {isFetching
            ? Array.from({ length: SKELETON_COUNT }, (_, i) => <S.SkeletonTile key={i} />)
            : images.map((image) => (
                <S.ImageCard key={image.id}>
                  <S.ImageTile
                    type="button"
                    $selected={selectedImage?.id === image.id}
                    onClick={() => setSelectedImage(image)}
                  >
                    <S.RadioWrapper>
                      <S.ImageRadio readOnly checked={selectedImage?.id === image.id} />
                    </S.RadioWrapper>
                    <S.Thumbnail src={image.url} alt={image.description} />
                  </S.ImageTile>
                  <S.DescriptionWrapper>
                    <Typography
                      as="span"
                      size="text-small"
                      color="black500"
                      textAlign="center"
                      breakLines={1}
                    >
                      {image.description}
                    </Typography>
                  </S.DescriptionWrapper>
                </S.ImageCard>
              ))}
        </S.ImageGrid>
      )}
    </S.BrowseModal>
  )
}
